// Displays a task -- ID comes from a parameter in the URL or data can be passed in state from prior
// page.
//
// If in URL, loads from database and puts in location.state.
// In state I have:
//    state.task
//    state.item
//    state.back : optional [Extras] for ItemBreadcrumbs
//
// Like the logbook-entry and trip detail pages, every field is directly
// editable here -- and autosaves: typed edits persist ~1.5s after the last
// keystroke or on leaving the field, discrete controls persist immediately,
// and the scheduling quick-set buttons write through the same per-task save
// lane (see `quickSave`). There is no Save button; the global SaveStatusPill
// reports progress and errors. Navigating away only prompts when an edit is
// held invalid or a save failed -- everything else flushes on the way out.

import { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useStatusMessage, Attachments, Confirm, DataCard, Field, StatusBadge } from '../../components';
import { ItemBreadcrumbs } from '../item/ItemBreadcrumbs';
import { taskFields } from '../../fleet-shared/TaskFields.mjs';
import { deleteTask, copyTaskToCompletedTasks, updateTask, listLogbooks, createLogbook } from '../../utilities/Database.mjs';
import { displayDate, databaseDateTimeString, datePlusDays, datePlusMonths } from '../../fleet-shared/Common.mjs';
import { addBucketNameToTasks, todayMidnight, dateMidnight, counterFromId } from '../../fleet-shared/TaskBuckets.mjs';
import { bucketNameAll, urlForItem, urlForLogbookEntryFromTask, urlForTaskStorage, urlForTasks, tasksListSegmentFromNavigationState } from '../../fleet-shared/Urls.mjs';
import { LOGBOOK_TYPE_MAINTENANCE, defaultNameForType } from '../../fleet-shared/LogbookTypes.mjs';
import { ChooseLogbookModal } from '../logbook/ChooseLogbookModal';
import { LogbookUpdateTask } from '../logbook/LogbookUpdateTask';
import { attachItemCounter } from './attachItemCounter';
import { CardHeader } from '../../components/CardHeader';
import { useUnsavedChangesGuard } from '../../utilities/useUnsavedChangesGuard';
import { useAutosave } from '../../utilities/useAutosave';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import { validateTask } from '../../utilities/Validators.mjs';

import { generateClient } from 'aws-amplify/api';
export const client = generateClient();

// Scheduling fields are pulled out of the plain field list and regrouped
// below it, so each "Due" line can be followed by its interval and its
// quick-set buttons.
const schedulingKeys = ['dueCounter', 'itemCounterId', 'dueDate', 'counterInterval', 'daysInterval'];
const counterKeys = ['dueCounter', 'itemCounterId', 'counterInterval'];
const dateKeys = ['dueDate', 'daysInterval'];

// The database stores "no priority" as -1 (see `Database.updateTask`), and
// legacy rows can carry the same sentinel for cost. Both mean "unset", so
// blank them out -- otherwise the editable fields would show a literal -1.
function withoutSentinels(task) {
  if (!task) return task;
  const normalized = { ...task };
  if (normalized.priority === -1) {
    normalized.priority = null;
  }
  if (normalized.cost === -1) {
    normalized.cost = null;
  }
  return normalized;
}

// This page's copy of the field registry: clone each entry (never mutate the
// shared one), fill the counter select's options from the item's counters, and
// spell out which interval is which -- the counter has its own interval field
// right above the date one here.
function buildTaskFields(item) {
  return taskFields.map((field) => {
    const copy = { ...field };
    if (copy.key === 'itemCounterId') {
      const counters = item?.itemCounter?.items ?? [];
      copy.selects = counters.map((counter, index) => ({
        // index + 1 to make room for no-counter, unshifted below.
        id: counter.id,
        index: index + 1,
        name: counter.name,
      }));
      copy.selects.unshift({ id: null, index: 0, name: '(None)' });
    } else if (copy.key === 'daysInterval') {
      copy.name = 'Date Interval';
    }
    return copy;
  });
}

// A row of quick-set buttons, borrowing Field's label/control geometry so the
// buttons line up under the inputs above them.
const QuickSetRow = ({ label, children }) => (
  <div className="container">
    <div className="d-flex flex-row align-items-center">
      <span className="col-4 d-flex justify-content-end align-items-center pe-3">{label}</span>
      <div className="col-8 d-flex flex-row flex-wrap align-items-center gap-2">
        {children}
      </div>
    </div>
  </div>
);

// Quick-set buttons for the counter: the counter's current reading, and that
// reading plus the task's interval. Only shown when the task points at a
// counter we can read. Everything here is derived from the *saved* task, since
// that's what a click persists -- an unsaved counter/interval edit sitting in
// the form doesn't move these buttons until it's saved.
const CounterQuickSet = ({ task, item, onQuickSave }) => {
  const currentCounter = task.itemCounterId ? counterFromId(item, task.itemCounterId) : -1;
  if (task.itemCounterId == null || currentCounter === -1) {
    return null;
  }

  return (
    <QuickSetRow label="Set Counter To:">
      <Button size="sm" variant="outline-primary" onClick={() => onQuickSave({ dueCounter: currentCounter })}>
        {currentCounter}
      </Button>
      { task.counterInterval > 0 && (
        <Button size="sm" variant="outline-primary" onClick={() => onQuickSave({ dueCounter: currentCounter + task.counterInterval })}>
          {currentCounter + task.counterInterval}
        </Button>
      )}
    </QuickSetRow>
  );
};

// Quick-set buttons for the due date: today, plus "today + interval" and "due
// date + interval" when the task recurs. Months take precedence over days
// (only one is ever set). Same "derived from the saved task" rule as the
// counter buttons above.
//
// Two of those can land on one day -- a task due today makes both interval
// dates the same, and a task overdue by exactly one interval rolls its due
// date forward onto today -- so a candidate landing on a day already offered
// is hidden. The first one wins, which is what keeps today labelled "Today"
// rather than as a date.
const DateQuickSet = ({ task, onQuickSave }) => {
  const hasInterval = task.daysInterval > 0 || task.monthsInterval > 0;
  const datePlusInterval = (start) => {
    if (task.monthsInterval > 0) {
      return datePlusMonths(start, task.monthsInterval);
    }
    if (task.daysInterval > 0) {
      return datePlusDays(start, task.daysInterval);
    }
    return null;
  };
  const today = todayMidnight();
  // Normalize the interval base to start-of-day so quick-set stores a date-only value
  // even when task.dueDate carries a legacy time of day. `today` is already midnight.
  const dueDatePlusInterval = (hasInterval && task.dueDate) ? datePlusInterval(dateMidnight(new Date(task.dueDate))) : null;
  const todayPlusInterval = hasInterval ? datePlusInterval(today) : null;

  // Every candidate is local midnight, so getTime() equality is same-day
  // equality (the comparison LogbookUpdateTask's radios use).
  const seen = new Set();
  const buttons = [
    { label: 'Today', date: today },
    dueDatePlusInterval && { label: displayDate(dueDatePlusInterval), date: dueDatePlusInterval },
    todayPlusInterval && { label: displayDate(todayPlusInterval), date: todayPlusInterval },
  ].filter((candidate) => {
    if (!candidate || seen.has(candidate.date.getTime())) {
      return false;
    }
    seen.add(candidate.date.getTime());
    return true;
  });

  return (
    <QuickSetRow label="Set Date To:">
      {buttons.map(({ label, date }) => (
        <Button key={date.getTime()} size="sm" variant="outline-primary" onClick={() => onQuickSave({ dueDate: databaseDateTimeString(date) })}>
          {label}
        </Button>
      ))}
    </QuickSetRow>
  );
};

const getTaskPogoTask = /* GraphQL */ `
    query GetTaskPogoTask(
      $itemId: ID!
      $taskId: ID!) 
    {
      getTaskPogoItem(id: $itemId) {
        id
        name
        soonDays
        soonCounter
        itemCounter {
          items {
            id
            name
            counter
          }
          nextToken
        }
      }

      getTaskPogoTask(id: $taskId) {
        id
        name
        assignedEmail
        priority
        rank
        cost
        itemId
        item {
          id
          name
          parentId
        }
        description
        notes
        dueCounter
        dueDate
        counterInterval
        daysInterval
        monthsInterval
        itemCounterId
        itemCounter {
          name
          counter
        }
      }
    }
  `;

const Task = () => {
  // For URL management, state.
  const navigate = useNavigate();
  const location = useLocation();

  // Passed on URL:
  const { itemId, taskId } = useParams();

  // State for data
  const [item, setItem] = useState(null);
  const [task, setTask] = useState(null);

  // Confirm delete
  const [showConfirm, setShowConfirm] = useState(false);

  // Add-to-logbook flow: choosing / creating a Maintenance Log, or completing
  // the task without a logbook entry (the roll-forward step).
  const [maintenanceLogbooks, setMaintenanceLogbooks] = useState([]);
  const [showChooseLogbook, setShowChooseLogbook] = useState(false);
  const [showUpdateTaskOnly, setShowUpdateTaskOnly] = useState(false);

  // Status message
  const [StatusMessage, {showText, showError, showLoading, hideStatus}] = useStatusMessage();

  useEffect(() => {
    // Loads the item/task on first load.
    async function load() {
      try {
        showLoading();
        var taskData = await client.graphql({ 
          query: getTaskPogoTask, 
          variables: {
            itemId: itemId, 
            taskId: taskId 
          } 
        });

        // Go get the data from the database.
        const item = taskData.data.getTaskPogoItem;
        const task = withoutSentinels(taskData.data.getTaskPogoTask);

        addBucketNameToTasks([task], item);

        // Set the state.
        setItem(item);
        setTask(task);  

        // Hide the loading message.
        hideStatus();
      } catch (err) {
        showError(err);
      }
    }
    load();
  }, [taskId, itemId, hideStatus, showError, showLoading]);

  const fields = item ? buildTaskFields(item) : [];
  const recordKey = `task:${taskId}`;

  // Autosave: every field edit is pushed through the shared save queue.
  // The save keeps the server's response (not the form payload) so anything
  // it normalizes -- e.g. an emptied priority coming back as the -1
  // sentinel -- lands on the page. `updateTask` returns a scalar-only task,
  // so resolve the counter back onto it and re-bucket before it goes into
  // state.
  const {
    formState,
    setFormState: updateFormState,
    fieldErrors,
    flush,
    guardDirty,
  } = useAutosave({
    recordKey,
    original: task,
    fields,
    validate: validateTask,
    save: async (payload) => {
      payload.id = task.id;
      payload.itemId = task.itemId;
      const saved = withoutSentinels(await updateTask(payload, item));
      await attachItemCounter(saved, task, item);
      addBucketNameToTasks([saved], item);
      return saved;
    },
    onSaved: (saved) => {
      if (saved) setTask(saved);
    },
  });

  // Warn before navigating away only when edits could actually be lost: an
  // invalid (held) edit or a failed save. Plain pending edits flush on the
  // way out and finish through the module-level queue.
  const { promptOpen: showLeavePrompt, confirmLeave, cancelLeave, markClean } = useUnsavedChangesGuard(guardDirty);

  // The task is gone (deleted, or completed without a logbook entry), so any
  // unsaved edits went with it -- cancel anything queued (a late save
  // against the deleted row must not fire) and don't prompt on the way out.
  // markClean() applies synchronously since we navigate in this same tick.
  const navigateAfterTaskRemoved = () => {
    saveQueue.cancel(recordKey);
    markClean();
    const segment = tasksListSegmentFromNavigationState(location.state);
    const url = segment ? urlForTasks(itemId, segment) : urlForItem(itemId);
    navigate(url, { replace: true });
  };

  // Confirm delete, then delete item.
  function deleteTapped() {
    setShowConfirm(true);
  }

  // User confirmed they want to delete this item.
  async function onConfirmDelete() {
    try {
      showText('Deleting...');
      setShowConfirm(false);

      // Cancel any pending or in-flight autosave first -- a late update
      // against the deleted row must neither error nor resurrect it.
      saveQueue.cancel(recordKey);

      // Delete the item and all its children:
      await deleteTask(task.id);

      // Status is good:
      hideStatus();

      navigateAfterTaskRemoved();
    } catch (err) {
      setShowConfirm(false);
      showError(err);
    }
  }

  // TODO: Add email logbook entry
  // function emailTapped() {
  //   console.log('email tapped');
  // }

  // Copy the task into the given logbook and go edit the new entry.
  async function proceedWithLogbook(logbook) {
    try {
      setShowChooseLogbook(false);
      showText('Adding...');
      const newTask = await copyTaskToCompletedTasks(task, item, logbook.id);
      navigate(urlForLogbookEntryFromTask(itemId, logbook.id, newTask.id));
    } catch (err) {
      // Without this, a failed create left the page stuck on "Adding..."
      // with no feedback (the error surfaced only as an unhandled rejection).
      showError(err);
    }
  }

  // "Add to Logbook" tapped. Resolve which Maintenance Log to use: exactly one
  // -> use it; several -> ask; none -> offer to create (or complete without an
  // entry). Only Maintenance logbooks take part in task completion.
  async function addToLogbook() {
    try {
      showText('Loading logbooks...');
      const all = await listLogbooks(item.id);
      const maintenance = all.filter((lb) => lb.logbookType === LOGBOOK_TYPE_MAINTENANCE);
      hideStatus();
      if (maintenance.length === 1) {
        await proceedWithLogbook(maintenance[0]);
        return;
      }
      setMaintenanceLogbooks(maintenance);
      setShowChooseLogbook(true);
    } catch (err) {
      showError(err);
    }
  }

  // User chose to create a fresh Maintenance Log for this entry.
  async function createLogbookAndProceed() {
    try {
      showText('Creating logbook...');
      const created = await createLogbook({
        itemId: item.id,
        name: defaultNameForType(LOGBOOK_TYPE_MAINTENANCE),
        logbookType: LOGBOOK_TYPE_MAINTENANCE,
      });
      await proceedWithLogbook(created);
    } catch (err) {
      showError(err);
    }
  }

  // User chose to complete the task without a logbook entry -- go straight to
  // the roll-forward / delete step.
  function completeWithoutEntry() {
    setShowChooseLogbook(false);
    setShowUpdateTaskOnly(true);
  }

  // Quick-set buttons apply their patch to the form and save immediately,
  // through the same autosave lane and payload builder as every other edit.
  // (Scheduling a separate hand-built job on the lane would REPLACE a typed
  // edit still sitting in its debounce window -- the queue keeps only the
  // latest pending job per record.) Any mid-edit change rides along with the
  // click, which under autosave it would have done moments later anyway.
  function quickSave(patch) {
    updateFormState({ ...formState, ...patch }, { immediate: true });
  }

  const fieldsFor = (keys) => keys.map((key) => fields.find((f) => f.key === key)).filter(Boolean);
  const plainFields = fields.filter((f) => !schedulingKeys.includes(f.key));

  // TODO: Links to itemId, itemCounterId, taskId?

  return (
    <div>
      <ItemBreadcrumbs item={item} extras={[{name: 'Tasks', url: urlForTasks(itemId, tasksListSegmentFromNavigationState(location.state) ?? bucketNameAll)}]} />

      <StatusMessage />

      { 
        item && task && formState && (
          <div>
            <DataCard
              header={
                <CardHeader
                  text={task.name}
                  belowText=<StatusBadge status={task.bucketName}/>
                  addTapped={addToLogbook}
                  addText='Add to Logbook'
                />
              }
              body={
                <div>
                  {/* Leaving any field flushes the autosave debounce -- onBlur
                      bubbles up from the inputs to this container. */}
                  <div className="container" onBlur={() => flush()}>
                    {[...plainFields, ...fieldsFor(counterKeys)].map((field, index) => (
                      <Field
                        key={field.key}
                        field={field}
                        state={formState}
                        setState={updateFormState}
                        setFocus={index === 0}
                        error={fieldErrors[field.key]}
                      />
                    ))}
                    <CounterQuickSet task={task} item={item} onQuickSave={quickSave} />

                    {fieldsFor(dateKeys).map((field) => (
                      <Field
                        key={field.key}
                        field={field}
                        state={formState}
                        setState={updateFormState}
                        error={fieldErrors[field.key]}
                      />
                    ))}
                    <DateQuickSet task={task} onQuickSave={quickSave} />
                  </div>

                  <div className="container m-2 d-flex justify-content-center align-items-center gap-2 flex-wrap">
                    <Button variant="danger" onClick={deleteTapped}>
                      Delete
                    </Button>
                  </div>
                </div>
              }
            />

            <Attachments bucket={urlForTaskStorage(itemId, task.id)} />

          </div>
        )
      }

      <ChooseLogbookModal
        show={showChooseLogbook}
        onHide={() => setShowChooseLogbook(false)}
        logbooks={maintenanceLogbooks}
        onChoose={proceedWithLogbook}
        onCreateNew={createLogbookAndProceed}
        onSkip={completeWithoutEntry}
      />

      { task && (
        <Modal show={showUpdateTaskOnly} onHide={() => setShowUpdateTaskOnly(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Complete Task</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <LogbookUpdateTask
              task={task}
              item={item}
              onTaskDeleted={navigateAfterTaskRemoved}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowUpdateTaskOnly(false)}>Done</Button>
          </Modal.Footer>
        </Modal>
      )}

      <Confirm
        show={showConfirm}
        onConfirm={onConfirmDelete}
        onHide = {() => setShowConfirm(false)}
        body='Are you sure?'
        confirmText='Confirm Delete'
        title={`Delete`}>
      </Confirm>

      <Confirm
        show={showLeavePrompt}
        onConfirm={confirmLeave}
        onHide={cancelLeave}
        body="Some changes couldn't be saved. Leave and discard them?"
        cancelText='Stay'
        confirmText='Discard Changes'
        title='Unsaved Changes'>
      </Confirm>

    </div>
  )
}

export default Task;
